import { generateClient } from 'aws-amplify/data';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import awsConfig from '../../../amplify_outputs.json';

/**
 * @type {import('aws-amplify/data').Client<import('../../../amplify/data/resource').Schema>}
 */
const client = generateClient();

export const createUser = createAsyncThunk(
  'management/createUser',
  async (
    {
      email,
      type,
      fullName,
      companyName,
      invite,
      company,
      financial,
      cyber,
      individual,
      screening,
    },
    { rejectWithValue }
  ) => {
    try {
      let userType = null;
      switch (type.toLowerCase()) {
        case 'customer':
          userType = 'Customer';
          break;
        case 'analyst':
          userType = 'Analyst';
          break;
        case 'admin':
          userType = 'Admin';
          break;
        default:
          return rejectWithValue(`Invalid user type: ${type}`);
      }

      const checkUserResponse = await client.models.User.list({
        filter: { email: { eq: email } },
      });

      if (checkUserResponse.data && checkUserResponse.data.length > 0) {
        return rejectWithValue('User with this email already exists.');
      }

      const response = await client.queries.inviteUser({
        authMode: 'apiKey',
        authToken: awsConfig.data.api_key,
        fullName,
        type: userType,
        email,
        companyName,
        invite,
        userPoolId: awsConfig.auth.user_pool_id,

        company: company.available,
        companyEnabled: company.enabled,
        financial: financial.available,
        financialEnabled: financial.enabled,
        cyber: cyber.available,
        cyberEnabled: cyber.enabled,
        individual: individual.available,
        individualEnabled: individual.enabled,
        screening: screening.available,
        screeningEnabled: screening.enabled,
      });

      const parsedData = JSON.parse(response.data);

      return parsedData.body;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message || 'An error occurred during user creation.');
    }
  }
);

export const reInviteUser = createAsyncThunk(
  'management/reInviteUser',
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await client.queries.reInviteUser({
        authToken: awsConfig.data.api_key,
        email,
        userPoolId: awsConfig.auth.user_pool_id,
      });

      console.log('REINVITE RESPONSE', response);

      const parsedData = JSON.parse(response.data);

      return parsedData.body;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message || 'An error occurred during user creation.');
    }
  }
);

export const updateUser = createAsyncThunk(
  'management/updateUser',
  async (
    { id, fullName, email, companyName, company, financial, cyber, individual, screening },
    { rejectWithValue }
  ) => {
    try {
      const response = await client.models.User.update({
        id,
        fullName,
        email,
        companyName,
        company,
        financial,
        cyber,
        individual,
        screening,
      });

      const parsedData = JSON.parse(response.data);

      return parsedData.body;
    } catch (error) {
      console.error('Error updating user:', error);
      return rejectWithValue(error.message || 'An error occurred during user update.');
    }
  }
);

export const setTempAdmin = createAsyncThunk(
  'management/setTempAdmin',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await client.models.User.update({
        id,
        tempAdmin: status,
      });

      const parsedData = JSON.parse(response.data);

      return parsedData.body;
    } catch (error) {
      console.error('Error updating user:', error);
      return rejectWithValue(error.message || 'An error occurred during user update.');
    }
  }
);

export const lockUser = createAsyncThunk(
  'management/lockUser',
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await client.queries.lockUser({
        authMode: 'apiKey',
        authToken: awsConfig.data.api_key,
        userPoolId: awsConfig.auth.user_pool_id,
        userId: id,
      });

      const parsedData = JSON.parse(response.data);

      return parsedData.body;
    } catch (error) {
      console.error('Error updating user:', error);
      return rejectWithValue(error.message || 'An error occurred during user update.');
    }
  }
);

export const unlockUser = createAsyncThunk(
  'management/unlockUser',
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await client.queries.unlockUser({
        authMode: 'apiKey',
        authToken: awsConfig.data.api_key,
        userPoolId: awsConfig.auth.user_pool_id,
        userId: id,
      });

      const parsedData = JSON.parse(response.data);

      return parsedData.body;
    } catch (error) {
      console.error('Error updating user:', error);
      return rejectWithValue(error.message || 'An error occurred during user update.');
    }
  }
);

export const fetchUsers = createAsyncThunk(
  'management/fetchUsers',
  async ({ page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await client.models.User.list({
        authMode: 'apiKey',
        authToken: awsConfig.data.api_key,
        page,
        limit,
      });

      return response.data;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message || 'An error occurred while fetching users.');
    }
  }
);

export const fetchCustomers = createAsyncThunk(
  'management/fetchCustomers',
  async ({ page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await client.models.User.list({
        authMode: 'apiKey',
        authToken: awsConfig.data.api_key,
        page,
        limit,
        filter: { type: { eq: 'CUSTOMER' } },
      });

      return response.data;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message || 'An error occurred while fetching users.');
    }
  }
);

export const fetchAnalysts = createAsyncThunk(
  'management/fetchAnalysts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await client.models.User.list({
        authMode: 'apiKey',
        authToken: awsConfig.data.api_key,
        filter: { type: { eq: 'ANALYST' } },
      });

      return response.data;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message || 'An error occurred while fetching users.');
    }
  }
);

export const fetchUser = createAsyncThunk(
  'management/fetchUser',
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await client.models.User.get({ id });

      return response.data;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message || 'An error occurred while fetching users.');
    }
  }
);

const initialState = {
  loading: false,
  error: null,
  users: [],
  customers: [],
  analysts: [],
  user: null,
};

const managementSlice = createSlice({
  name: 'management',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchCustomers.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.loading = false;
        state.customers = action.payload;
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchAnalysts.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAnalysts.fulfilled, (state, action) => {
        state.loading = false;
        state.analysts = action.payload;
      })
      .addCase(fetchAnalysts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(fetchUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default managementSlice.reducer;
